<template>
  <div v-if="authStore.showLoginModal" class="modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 active">
    <div class="glass-card p-6 max-w-md w-full">
      <div class="flex justify-between items-center mb-6">
        <h3 class="text-xl font-bold text-indigo-700">
          <i class="fas fa-mobile-alt mr-2"></i>手机号登录/注册
        </h3>
        <button @click="closeModal" class="text-gray-500 hover:text-gray-700">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <form @submit.prevent="handleLogin" class="space-y-4">
        <div>
          <label for="phone" class="block text-sm font-medium text-gray-700 mb-1">手机号</label>
          <input 
            type="tel" 
            id="phone" 
            v-model="loginForm.phone" 
            class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" 
            placeholder="请输入手机号"
            pattern="[0-9]{11}"
            maxlength="11"
            required
          />
        </div>

        <div>
          <label for="verification-code" class="block text-sm font-medium text-gray-700 mb-1">验证码</label>
          <div class="flex space-x-2">
            <input 
              type="text" 
              id="verification-code" 
              v-model="loginForm.verificationCode" 
              class="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" 
              placeholder="请输入验证码"
              maxlength="6"
              required
            />
            <button 
              type="button" 
              @click="sendVerificationCode" 
              :disabled="cooldown > 0 || !isValidPhone"
              class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {{ cooldown > 0 ? `${cooldown}秒后重发` : '获取验证码' }}
            </button>
          </div>
        </div>

        <div class="flex items-center">
          <input type="checkbox" id="agree-terms" v-model="loginForm.agreeTerms" class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" required />
          <label for="agree-terms" class="ml-2 block text-sm text-gray-700">我已阅读并同意<a href="#" class="text-indigo-600 hover:text-indigo-500">用户协议</a>和<a href="#" class="text-indigo-600 hover:text-indigo-500">隐私政策</a></label>
        </div>

        <div>
          <button 
            type="submit" 
            class="w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition"
            :disabled="isLoginProcessing || !loginForm.verificationCode || !isValidPhone || !loginForm.agreeTerms"
          >
            {{ isLoginProcessing ? '处理中...' : '登录/注册' }}
          </button>
        </div>

        <div class="text-center text-sm text-gray-600">
          登录或注册即表示您同意我们的服务条款和隐私政策
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted } from 'vue';
import { api } from '../utils/api';
import toast from '../utils/toast';
import { authStore } from '../utils/authStore';

const emit = defineEmits(['login-success']);

const isLoginProcessing = ref(false)
const cooldown = ref(0)
let cooldownTimer = null

// 登录表单
const loginForm = ref({
  phone: '',
  verificationCode: '',
  agreeTerms: false,
  key: ''
})

const closeModal = () => {
  authStore.hide();
};

// 发送验证码
const sendVerificationCode = async () => {
  if (!isValidPhone.value) {
    toast.show('请输入有效的手机号');
    return;
  }

  try {
    const data = await api.fetch(`/api/login/sendVerifySms?phone=${loginForm.value.phone}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    loginForm.value.key = data;
    cooldown.value = 60;
    cooldownTimer = setInterval(() => {
      cooldown.value -= 1;
      if (cooldown.value <= 0) {
        clearInterval(cooldownTimer);
      }
    }, 1000);
    toast.show('验证码已发送至您的手机');
  } catch (error) {
    // 错误已由api.js处理
    console.error('发送验证码失败:', error);
  }
};

// 处理登录
const handleLogin = async () => {
  if (!isValidPhone.value || !loginForm.value.verificationCode || !loginForm.value.agreeTerms) {
    toast.show('请填写完整信息并同意条款');
    return;
  }

  isLoginProcessing.value = true;
  try {
    const data = await api.fetch('/api/login/loginByVerifyCode', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        phone: loginForm.value.phone,
        verifyCode: loginForm.value.verificationCode,
        key: loginForm.value.key,
      }),
    });

    emit('login-success', data)
    closeModal()
  } catch (error) {
    // 错误已由api.js处理
    console.error('登录失败:', error);
  } finally {
    isLoginProcessing.value = false;
    loginForm.value.verificationCode = '';
  }
};

// 验证手机号
const isValidPhone = computed(() => /^1[3-9]\d{9}$/.test(loginForm.value.phone))

onUnmounted(() => {
  if (cooldownTimer) clearInterval(cooldownTimer)
})
</script>